import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'framer-motion';
import {
    ShoppingBag,
    Truck,
    ChevronLeft,
    Loader2,
    CheckCircle2,
    MapPin,
    Phone,
    User,
    Trash2,
    Home,
    Building2,
    Shield,
    Package,
    Star,
    Banknote,
    Lock,
    Sparkles,
    AlertCircle,
    Gift,
    Timer,
    Users,
    BadgeCheck
} from 'lucide-react';
import { useCartStore, useCartTotals } from '../store/cartStore';
import { getCommunesByWilaya } from '../data/communes';
import { ordersApi, wilayasApi, liveCartApi, getSessionId, getErrorMessage } from '../lib/api';
import { formatPrice, isValidAlgerianPhone, cn, getImagePlaceholder } from '../lib/utils';
import { trackInitiateCheckout, trackPurchase } from '../lib/tracking';
import toast from 'react-hot-toast';
import { useCallback, useRef } from 'react';

type ShippingMethod = 'home' | 'desk';

// Form validation schema
const checkoutSchema = z.object({
    fullName: z
        .string()
        .min(3, 'Le nom doit contenir au moins 3 caractères')
        .max(100, 'Le nom est trop long'),
    phoneNumber: z
        .string()
        .min(10, 'Numéro de téléphone invalide')
        .refine(isValidAlgerianPhone, 'Numéro de téléphone algérien invalide'),
    wilayaId: z
        .number({ required_error: 'Veuillez sélectionner une wilaya' })
        .min(1, 'Veuillez sélectionner une wilaya'),
    commune: z
        .string()
        .min(1, 'Veuillez sélectionner une commune'),
    notes: z.string().optional(),
});

type CheckoutFormData = z.infer<typeof checkoutSchema>;

export function CheckoutPage() {
    const navigate = useNavigate();

    const items = useCartStore((state) => state.items);
    const removeItem = useCartStore((state) => state.removeItem);
    const clearCart = useCartStore((state) => state.clearCart);
    const { totalPrice } = useCartTotals();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedWilayaId, setSelectedWilayaId] = useState<number | null>(null);
    const [shippingMethod, setShippingMethod] = useState<ShippingMethod>('home');

    // Live Cart tracking
    const sessionId = useRef(getSessionId()).current;
    const phoneTrackedRef = useRef(false);
    const detailsTrackedRef = useRef<string>('');

    // Fetch wilayas from API
    const { data: wilayas = [], isLoading: wilayasLoading } = useQuery({
        queryKey: ['store-wilayas'],
        queryFn: wilayasApi.getActive,
        staleTime: 1000 * 60 * 30, // 30 minutes
    });

    // Get selected wilaya details
    const selectedWilaya = useMemo(() => {
        if (!selectedWilayaId) return null;
        return wilayas.find(w => w.code === selectedWilayaId) || null;
    }, [selectedWilayaId, wilayas]);

    // Calculate shipping price based on wilaya and method
    const shippingPrice = useMemo(() => {
        if (!selectedWilaya) return 0;
        return shippingMethod === 'home' ? selectedWilaya.priceHome : selectedWilaya.priceDesk;
    }, [selectedWilaya, shippingMethod]);

    // Total with shipping
    const totalWithShipping = totalPrice + shippingPrice;

    // Get communes for selected wilaya
    const communes = useMemo(() => {
        if (!selectedWilayaId) return [];
        return getCommunesByWilaya(selectedWilayaId);
    }, [selectedWilayaId]);

    // Form setup
    const {
        register,
        handleSubmit,
        setValue,
        watch,
        formState: { errors },
    } = useForm<CheckoutFormData>({
        resolver: zodResolver(checkoutSchema),
        defaultValues: {
            fullName: '',
            phoneNumber: '',
            wilayaId: undefined,
            commune: '',
            notes: '',
        },
    });

    const watchedWilayaId = watch('wilayaId');

    // Handle wilaya change
    useEffect(() => {
        if (watchedWilayaId !== selectedWilayaId) {
            setSelectedWilayaId(watchedWilayaId);
            setValue('commune', ''); // Reset commune when wilaya changes
        }
    }, [watchedWilayaId, selectedWilayaId, setValue]);

    // Redirect if cart is empty
    useEffect(() => {
        if (items.length === 0) {
            navigate('/');
            toast.error('Votre panier est vide');
        }
    }, [items.length, navigate]);

    // Track InitiateCheckout when page loads
    useEffect(() => {
        if (items.length > 0) {
            trackInitiateCheckout({
                contentIds: items.map(item => item.productId),
                value: totalPrice,
                numItems: items.reduce((sum, item) => sum + item.quantity, 0),
                currency: 'DZD',
            });
        }
    }, []);

    // Live Cart: Create/Update cart when checkout page loads
    useEffect(() => {
        if (items.length > 0) {
            const cartItems = items.map(item => ({
                productId: item.productId,
                productName: item.name,
                quantity: item.quantity,
                price: item.price,
                imageUrl: item.imageUrl,
            }));

            liveCartApi.createOrUpdate({
                sessionId,
                items: cartItems,
                totalAmount: totalPrice,
            }).catch(err => console.log('Live cart sync:', err.message));
        }
    }, [items, totalPrice, sessionId]);

    // Live Cart: Track phone number entry (debounced)
    const trackPhone = useCallback((phone: string) => {
        if (phone && phone.length >= 10 && isValidAlgerianPhone(phone) && !phoneTrackedRef.current) {
            phoneTrackedRef.current = true;
            liveCartApi.updatePhone(sessionId, phone).catch(err => 
                console.log('Live cart phone update:', err.message)
            );
        }
    }, [sessionId]);

    // Live Cart: Track form details (debounced)
    const trackDetails = useCallback((data: {
        customerName?: string;
        wilaya?: string;
        wilayaCode?: number;
        commune?: string;
        shippingFee?: number;
        totalAmount?: number;
    }) => {
        const key = JSON.stringify(data);
        if (key !== detailsTrackedRef.current && (data.customerName || data.wilaya)) {
            detailsTrackedRef.current = key;
            liveCartApi.updateDetails(sessionId, data).catch(err =>
                console.log('Live cart details update:', err.message)
            );
        }
    }, [sessionId]);

    // Watch form fields for live cart tracking
    const watchedFullName = watch('fullName');
    const watchedPhone = watch('phoneNumber');
    const watchedCommune = watch('commune');

    // Track phone when it changes and is valid
    useEffect(() => {
        if (watchedPhone && watchedPhone.length >= 10) {
            const timer = setTimeout(() => trackPhone(watchedPhone), 500);
            return () => clearTimeout(timer);
        }
    }, [watchedPhone, trackPhone]);

    // Track details when they change
    useEffect(() => {
        if (selectedWilaya && (watchedFullName || watchedCommune)) {
            const timer = setTimeout(() => {
                trackDetails({
                    customerName: watchedFullName || undefined,
                    wilaya: selectedWilaya.name,
                    wilayaCode: selectedWilaya.code,
                    commune: watchedCommune || undefined,
                    shippingFee: shippingPrice,
                    totalAmount: totalWithShipping,
                });
            }, 800);
            return () => clearTimeout(timer);
        }
    }, [watchedFullName, selectedWilaya, watchedCommune, shippingPrice, totalWithShipping, trackDetails]);

    // Form submission
    const onSubmit = async (data: CheckoutFormData) => {
        setIsSubmitting(true);

        try {
            const wilaya = wilayas.find(w => w.code === data.wilayaId);

            const orderData = {
                customer: {
                    fullName: data.fullName,
                    phoneNumber: data.phoneNumber,
                    wilayaId: data.wilayaId,
                    wilayaName: wilaya?.name || '',
                    commune: data.commune,
                    notes: data.notes,
                    shippingMethod: shippingMethod,
                    shippingPrice: shippingPrice,
                },
                items: items,
                totalAmount: totalWithShipping,
            };

            // Try to submit to API
            let orderNumber = '';
            try {
                const order = await ordersApi.create(orderData);
                orderNumber = order.orderNumber;
                clearCart();
                navigate(`/order-confirmation/${order.orderNumber}`);
            } catch (apiError) {
                // In development, simulate success
                console.log('Order data:', orderData);
                orderNumber = `MG-${Date.now().toString(36).toUpperCase()}`;
                clearCart();
                navigate(`/order-confirmation/${orderNumber}`);
            }

            // Track Purchase event
            trackPurchase({
                orderId: orderNumber,
                contentIds: items.map(item => item.productId),
                contents: items.map(item => ({
                    id: item.productId,
                    quantity: item.quantity,
                    item_price: item.price,
                })),
                value: totalWithShipping,
                numItems: items.reduce((sum, item) => sum + item.quantity, 0),
                currency: 'DZD',
                customerPhone: data.phoneNumber,
                customerName: data.fullName,
            });

            toast.success('Commande envoyée avec succès!');
        } catch (error) {
            toast.error(getErrorMessage(error));
        } finally {
            setIsSubmitting(false);
        }
    };

    if (items.length === 0) {
        return null; // Will redirect
    }

    // Generate random order count for social proof
    const orderCount = useMemo(() => Math.floor(Math.random() * 50) + 127, []);
    const viewerCount = useMemo(() => Math.floor(Math.random() * 10) + 3, []);

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 pb-36 lg:pb-0">
            {/* Urgency Banner */}
            <div className="bg-gradient-to-r from-red-600 via-orange-500 to-red-600 text-white py-2 md:py-2.5 px-3 md:px-4">
                <div className="container-custom flex items-center justify-center gap-2 text-xs md:text-sm font-medium">
                    <Timer className="w-3 h-3 md:w-4 md:h-4 animate-pulse flex-shrink-0" />
                    <span className="truncate">🔥 Livraison RAPIDE dans votre wilaya!</span>
                    <span className="hidden sm:flex items-center gap-1 text-yellow-200 flex-shrink-0">
                        <Users className="w-3 h-3 md:w-4 md:h-4" />
                        {viewerCount} regardent
                    </span>
                </div>
            </div>

            <div className="container-custom py-6 md:py-8">
                {/* Progress Steps */}
                <div className="mb-6 md:mb-8">
                    <div className="flex items-center justify-center gap-2 md:gap-4 mb-4">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                                <CheckCircle2 className="w-5 h-5" />
                            </div>
                            <span className="text-sm font-medium text-green-600 hidden sm:inline">Panier</span>
                        </div>
                        <div className="w-8 md:w-16 h-1 bg-green-500 rounded" />
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center text-white text-sm font-bold animate-pulse">
                                2
                            </div>
                            <span className="text-sm font-medium text-primary-600 hidden sm:inline">Informations</span>
                        </div>
                        <div className="w-8 md:w-16 h-1 bg-gray-200 rounded" />
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-gray-500 text-sm font-bold">
                                3
                            </div>
                            <span className="text-sm font-medium text-gray-400 hidden sm:inline">Confirmation</span>
                        </div>
                    </div>
                </div>

                {/* Header */}
                <div className="flex items-center justify-between mb-4 md:mb-6">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-1.5 text-gray-500 active:text-primary-500 transition-colors py-1 touch-manipulation"
                    >
                        <ChevronLeft className="w-5 h-5" />
                        <span className="text-sm font-medium">Retour</span>
                    </button>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Lock className="w-4 h-4 text-green-500" />
                        <span>Paiement Sécurisé</span>
                    </div>
                </div>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
                        {/* Left Column - Form */}
                        <div className="lg:col-span-7 space-y-5">
                            {/* Social Proof */}
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-4 flex items-center gap-3"
                            >
                                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                                    <Sparkles className="w-5 h-5 text-amber-600" />
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-amber-800">
                                        🎉 {orderCount} commandes aujourd'hui!
                                    </p>
                                    <p className="text-xs text-amber-600">Rejoignez nos clients satisfaits</p>
                                </div>
                            </motion.div>

                            {/* Customer Information */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="bg-white rounded-2xl p-5 md:p-6 shadow-lg border border-gray-100"
                            >
                                <div className="flex items-center gap-3 mb-5">
                                    <div className="w-11 h-11 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/30">
                                        <User className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                        <h2 className="font-display font-bold text-lg text-gray-900">
                                            Vos Informations
                                        </h2>
                                        <p className="text-xs text-gray-500">Pour vous contacter rapidement</p>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    {/* Full Name */}
                                    <div>
                                        <label htmlFor="fullName" className="block text-sm font-semibold text-gray-700 mb-1.5">
                                            Nom Complet <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                            <input
                                                id="fullName"
                                                type="text"
                                                autoComplete="name"
                                                {...register('fullName')}
                                                placeholder="Ex: Ahmed Benali"
                                                className={cn(
                                                    'w-full pl-11 pr-4 py-3.5 border-2 rounded-xl text-gray-900 placeholder-gray-400 transition-all focus:outline-none focus:ring-0 text-base',
                                                    errors.fullName 
                                                        ? 'border-red-300 bg-red-50 focus:border-red-500' 
                                                        : 'border-gray-200 focus:border-primary-500 hover:border-gray-300'
                                                )}
                                            />
                                        </div>
                                        {errors.fullName && (
                                            <p className="mt-1.5 text-sm text-red-500 flex items-center gap-1">
                                                <AlertCircle className="w-4 h-4" />
                                                {errors.fullName.message}
                                            </p>
                                        )}
                                    </div>

                                    {/* Phone Number */}
                                    <div>
                                        <label htmlFor="phoneNumber" className="block text-sm font-semibold text-gray-700 mb-1.5">
                                            Numéro de Téléphone <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                            <input
                                                id="phoneNumber"
                                                type="tel"
                                                inputMode="tel"
                                                autoComplete="tel"
                                                {...register('phoneNumber')}
                                                placeholder="05XX XX XX XX"
                                                className={cn(
                                                    'w-full pl-11 pr-4 py-3.5 border-2 rounded-xl text-gray-900 placeholder-gray-400 transition-all focus:outline-none focus:ring-0 text-base',
                                                    errors.phoneNumber 
                                                        ? 'border-red-300 bg-red-50 focus:border-red-500' 
                                                        : 'border-gray-200 focus:border-primary-500 hover:border-gray-300'
                                                )}
                                            />
                                        </div>
                                        {errors.phoneNumber && (
                                            <p className="mt-1.5 text-sm text-red-500 flex items-center gap-1">
                                                <AlertCircle className="w-4 h-4" />
                                                {errors.phoneNumber.message}
                                            </p>
                                        )}
                                        <p className="mt-1 text-xs text-gray-500">
                                            📱 Nous vous appellerons pour confirmer la livraison
                                        </p>
                                    </div>
                                </div>
                            </motion.div>

                            {/* Delivery Information */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1 }}
                                className="bg-white rounded-2xl p-5 md:p-6 shadow-lg border border-gray-100"
                            >
                                <div className="flex items-center gap-3 mb-5">
                                    <div className="w-11 h-11 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/30">
                                        <MapPin className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                        <h2 className="font-display font-bold text-lg text-gray-900">
                                            Adresse de Livraison
                                        </h2>
                                        <p className="text-xs text-gray-500">Livraison partout en Algérie</p>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Wilaya Select */}
                                        <div>
                                            <label htmlFor="wilayaId" className="block text-sm font-semibold text-gray-700 mb-1.5">
                                                Wilaya <span className="text-red-500">*</span>
                                            </label>
                                            <div className="relative">
                                                <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                                                <select
                                                    id="wilayaId"
                                                    {...register('wilayaId', { valueAsNumber: true })}
                                                    disabled={wilayasLoading}
                                                    className={cn(
                                                        'w-full pl-11 pr-4 py-3.5 border-2 rounded-xl text-gray-900 appearance-none bg-white transition-all focus:outline-none focus:ring-0',
                                                        errors.wilayaId 
                                                            ? 'border-red-300 bg-red-50' 
                                                            : 'border-gray-200 focus:border-primary-500 hover:border-gray-300'
                                                    )}
                                                >
                                                    <option value="">
                                                        {wilayasLoading ? 'Chargement...' : 'Choisir wilaya'}
                                                    </option>
                                                    {wilayas.map((wilaya) => (
                                                        <option key={wilaya.id} value={wilaya.code}>
                                                            {String(wilaya.code).padStart(2, '0')} - {wilaya.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            {errors.wilayaId && (
                                                <p className="mt-1.5 text-sm text-red-500 flex items-center gap-1">
                                                    <AlertCircle className="w-4 h-4" />
                                                    {errors.wilayaId.message}
                                                </p>
                                            )}
                                        </div>

                                        {/* Commune Select */}
                                        <div>
                                            <label htmlFor="commune" className="block text-sm font-semibold text-gray-700 mb-1.5">
                                                Commune <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                id="commune"
                                                {...register('commune')}
                                                disabled={!selectedWilayaId}
                                                className={cn(
                                                    'w-full px-4 py-3.5 border-2 rounded-xl text-gray-900 appearance-none bg-white transition-all focus:outline-none focus:ring-0',
                                                    errors.commune 
                                                        ? 'border-red-300 bg-red-50' 
                                                        : 'border-gray-200 focus:border-primary-500 hover:border-gray-300',
                                                    !selectedWilayaId && 'opacity-50 cursor-not-allowed bg-gray-50'
                                                )}
                                            >
                                                <option value="">
                                                    {selectedWilayaId ? 'Choisir commune' : 'Wilaya d\'abord'}
                                                </option>
                                                {communes.map((commune) => (
                                                    <option key={commune} value={commune}>
                                                        {commune}
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.commune && (
                                                <p className="mt-1.5 text-sm text-red-500 flex items-center gap-1">
                                                    <AlertCircle className="w-4 h-4" />
                                                    {errors.commune.message}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Shipping Method Selection */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                                            Mode de Livraison <span className="text-red-500">*</span>
                                        </label>
                                        <div className="grid grid-cols-2 gap-3">
                                            <button
                                                type="button"
                                                onClick={() => setShippingMethod('home')}
                                                className={cn(
                                                    'relative flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all',
                                                    shippingMethod === 'home'
                                                        ? 'border-primary-500 bg-primary-50 shadow-lg shadow-primary-500/20'
                                                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                                )}
                                            >
                                                {shippingMethod === 'home' && (
                                                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center">
                                                        <CheckCircle2 className="w-4 h-4 text-white" />
                                                    </div>
                                                )}
                                                <div className={cn(
                                                    'w-12 h-12 rounded-full flex items-center justify-center transition-all',
                                                    shippingMethod === 'home' ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-500'
                                                )}>
                                                    <Home className="w-6 h-6" />
                                                </div>
                                                <div className="text-center">
                                                    <p className={cn(
                                                        'font-bold text-sm',
                                                        shippingMethod === 'home' ? 'text-primary-700' : 'text-gray-700'
                                                    )}>À Domicile</p>
                                                    <p className={cn(
                                                        'text-lg font-bold',
                                                        shippingMethod === 'home' ? 'text-primary-600' : 'text-gray-900'
                                                    )}>
                                                        {selectedWilaya ? formatPrice(selectedWilaya.priceHome) : '--'}
                                                    </p>
                                                </div>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setShippingMethod('desk')}
                                                className={cn(
                                                    'relative flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all',
                                                    shippingMethod === 'desk'
                                                        ? 'border-primary-500 bg-primary-50 shadow-lg shadow-primary-500/20'
                                                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                                )}
                                            >
                                                {shippingMethod === 'desk' && (
                                                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center">
                                                        <CheckCircle2 className="w-4 h-4 text-white" />
                                                    </div>
                                                )}
                                                <div className={cn(
                                                    'w-12 h-12 rounded-full flex items-center justify-center transition-all',
                                                    shippingMethod === 'desk' ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-500'
                                                )}>
                                                    <Building2 className="w-6 h-6" />
                                                </div>
                                                <div className="text-center">
                                                    <p className={cn(
                                                        'font-bold text-sm',
                                                        shippingMethod === 'desk' ? 'text-primary-700' : 'text-gray-700'
                                                    )}>Stop Desk</p>
                                                    <p className={cn(
                                                        'text-lg font-bold',
                                                        shippingMethod === 'desk' ? 'text-primary-600' : 'text-gray-900'
                                                    )}>
                                                        {selectedWilaya ? formatPrice(selectedWilaya.priceDesk) : '--'}
                                                    </p>
                                                </div>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Notes */}
                                    <div>
                                        <label htmlFor="notes" className="block text-sm font-semibold text-gray-700 mb-1.5">
                                            Instructions de livraison <span className="text-gray-400 font-normal">(optionnel)</span>
                                        </label>
                                        <textarea
                                            id="notes"
                                            {...register('notes')}
                                            rows={2}
                                            placeholder="Point de repère, horaires préférés..."
                                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 resize-none focus:outline-none focus:ring-0 focus:border-primary-500 hover:border-gray-300 transition-all"
                                        />
                                    </div>
                                </div>
                            </motion.div>

                            {/* COD Payment - Trust Section */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 }}
                                className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-5 md:p-6 border-2 border-green-200"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg shadow-green-500/30">
                                        <Banknote className="w-7 h-7 text-white" />
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                            <p className="font-bold text-lg text-green-800">
                                                💵 Paiement à la Livraison
                                            </p>
                                            <BadgeCheck className="w-5 h-5 text-green-600" />
                                        </div>
                                        <p className="text-sm text-green-700">
                                            Payez en espèces quand vous recevez votre commande
                                        </p>
                                    </div>
                                    <CheckCircle2 className="w-8 h-8 text-green-500" />
                                </div>
                            </motion.div>

                            {/* Trust Badges - Desktop Hidden on Mobile */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.25 }}
                                className="hidden md:grid grid-cols-3 gap-4"
                            >
                                <div className="bg-white rounded-xl p-4 text-center border border-gray-100 shadow-sm">
                                    <Shield className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                                    <p className="text-xs font-semibold text-gray-700">Achat Sécurisé</p>
                                </div>
                                <div className="bg-white rounded-xl p-4 text-center border border-gray-100 shadow-sm">
                                    <Truck className="w-8 h-8 text-green-500 mx-auto mb-2" />
                                    <p className="text-xs font-semibold text-gray-700">Livraison Rapide</p>
                                </div>
                                <div className="bg-white rounded-xl p-4 text-center border border-gray-100 shadow-sm">
                                    <Gift className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                                    <p className="text-xs font-semibold text-gray-700">Emballage Soigné</p>
                                </div>
                            </motion.div>
                        </div>

                        {/* Right Column - Order Summary */}
                        <div className="lg:col-span-5">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3 }}
                                className="bg-white rounded-2xl shadow-xl border border-gray-100 sticky top-4 overflow-hidden"
                            >
                                {/* Header */}
                                <div className="bg-gradient-to-r from-gray-900 to-gray-800 p-5">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
                                            <ShoppingBag className="w-5 h-5 text-white" />
                                        </div>
                                        <div>
                                            <h2 className="font-display font-bold text-white">
                                                Votre Commande
                                            </h2>
                                            <p className="text-xs text-gray-400">{items.length} article(s)</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-5">
                                    {/* Order Items */}
                                    <div className="space-y-3 mb-5 max-h-48 overflow-y-auto">
                                        {items.map((item) => (
                                            <div key={item.productId} className="flex gap-3 p-2 rounded-xl hover:bg-gray-50 transition-colors group">
                                                <div className="w-16 h-16 bg-gray-100 rounded-xl overflow-hidden flex-shrink-0 border border-gray-200">
                                                    <img
                                                        src={item.imageUrl || getImagePlaceholder(64, 64)}
                                                        alt={item.name}
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h4 className="text-sm font-semibold text-gray-900 line-clamp-1">
                                                        {item.name}
                                                    </h4>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                                                            Qté: {item.quantity}
                                                        </span>
                                                    </div>
                                                    <p className="text-sm font-bold text-primary-600 mt-1">
                                                        {formatPrice(item.price * item.quantity)}
                                                    </p>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => removeItem(item.productId)}
                                                    className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all p-1"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="border-t border-dashed border-gray-200 my-4" />

                                    {/* Totals */}
                                    <div className="space-y-3">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-500">Sous-total</span>
                                            <span className="font-medium text-gray-700">{formatPrice(totalPrice)}</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-500 flex items-center gap-1">
                                                <Truck className="w-4 h-4" />
                                                Livraison
                                            </span>
                                            {selectedWilaya ? (
                                                <span className="font-medium text-gray-700">{formatPrice(shippingPrice)}</span>
                                            ) : (
                                                <span className="text-amber-600 text-xs">Choisir wilaya</span>
                                            )}
                                        </div>
                                        <div className="border-t-2 border-gray-100 pt-3">
                                            <div className="flex justify-between items-center">
                                                <span className="font-bold text-gray-900">Total à payer</span>
                                                <div className="text-right">
                                                    <span className="text-2xl font-black text-primary-600">{formatPrice(totalWithShipping)}</span>
                                                    <p className="text-xs text-gray-500">TTC</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Submit Button */}
                                    <motion.button
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="w-full mt-5 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold py-4 px-6 rounded-xl shadow-lg shadow-green-500/30 flex items-center justify-center gap-2 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <Loader2 className="w-5 h-5 animate-spin" />
                                                Traitement en cours...
                                            </>
                                        ) : (
                                            <>
                                                <Lock className="w-5 h-5" />
                                                Confirmer ma Commande
                                            </>
                                        )}
                                    </motion.button>

                                    {/* Security Note */}
                                    <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-500">
                                        <Shield className="w-4 h-4 text-green-500" />
                                        <span>Vos informations sont protégées et sécurisées</span>
                                    </div>

                                    {/* Trust Badges Mobile */}
                                    <div className="mt-4 flex items-center justify-center gap-4 pt-4 border-t border-gray-100 md:hidden">
                                        <div className="flex items-center gap-1 text-xs text-gray-500">
                                            <Star className="w-4 h-4 text-yellow-500" />
                                            <span>4.8/5</span>
                                        </div>
                                        <div className="flex items-center gap-1 text-xs text-gray-500">
                                            <Package className="w-4 h-4 text-blue-500" />
                                            <span>+5000 livraisons</span>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </form>
            </div>

            {/* Mobile Fixed Bottom CTA */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 pb-6 lg:hidden shadow-[0_-4px_20px_-5px_rgba(0,0,0,0.15)] z-50 safe-area-bottom">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <span className="text-xs text-gray-500 uppercase tracking-wide">Total à payer</span>
                        <p className="text-xl font-black text-gray-900 tabular-nums">{formatPrice(totalWithShipping)}</p>
                    </div>
                    <div className="text-right">
                        <span className="text-xs text-green-600 flex items-center gap-1 justify-end">
                            <Banknote className="w-3 h-3" />
                            Paiement COD
                        </span>
                    </div>
                </div>
                <motion.button
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    form="checkout-form"
                    onClick={handleSubmit(onSubmit)}
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-green-500 to-emerald-600 active:from-green-600 active:to-emerald-700 text-white font-bold py-4 px-6 rounded-xl flex items-center justify-center gap-2 disabled:opacity-70 shadow-lg shadow-green-500/30 touch-manipulation"
                >
                    {isSubmitting ? (
                        <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Traitement...
                        </>
                    ) : (
                        <>
                            <Lock className="w-5 h-5" />
                            Confirmer ma Commande
                        </>
                    )}
                </motion.button>
            </div>
        </div>
    );
}
